import { useMemo, useState } from "react";
import type { Account } from "@veto/domain";
import { MAX_ABOUT_CHARS, MAX_PROFILE_NAME_CHARS, type ProfileMedia } from "@veto/protocol";
import { AvatarPicker } from "../components/AvatarPicker";
import type { ProfileInput } from "../hooks/useProfileActions";

/**
 * Profile editor — avatar (photo / GIF / short video), name, bio.
 * Lives in its own screen (ProfileModal); save persists locally first,
 * then fans out one encrypted update per contact over the ratchet sessions.
 */
export function ProfileTab({
  account,
  onSaveProfile,
  onToast,
  onClose,
}: {
  account: Account;
  onSaveProfile: (input: ProfileInput) => Promise<void>;
  onToast: (msg: string) => void;
  /** Provided when hosted in the standalone profile screen. */
  onClose?: () => void;
}) {
  const [displayName, setDisplayName] = useState(account.displayName);
  const [about, setAbout] = useState(account.about ?? "");
  const [avatar, setAvatar] = useState<ProfileMedia | null>(account.avatar ?? null);
  const [busy, setBusy] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const dirty = useMemo(() => {
    if (displayName !== account.displayName) return true;
    if (about !== (account.about ?? "")) return true;
    return (avatar?.dataB64 ?? null) !== (account.avatar?.dataB64 ?? null);
  }, [displayName, about, avatar, account]);

  const nameOk = displayName.trim().length > 0;

  function reset() {
    setDisplayName(account.displayName);
    setAbout(account.about ?? "");
    setAvatar(account.avatar ?? null);
  }

  async function save() {
    if (!nameOk || busy) return;
    setBusy(true);
    setSavedTick(false);
    try {
      await onSaveProfile({ displayName, about, avatar });
      setSavedTick(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="profile-editor stack">
      <div className="profile-editor-top">
        <AvatarPicker
          name={displayName}
          value={avatar}
          onChange={(m) => {
            setAvatar(m);
            setSavedTick(false);
          }}
          size={104}
          onError={onToast}
        />
        <div className="profile-editor-topmeta">
          <strong className="profile-name">{displayName.trim() || "Your profile"}</strong>
          <span className="muted text-sm">
            Photo, GIF or video loop up to 3 MiB (video ≤ 6s). Photos are auto-cropped locally —
            the original never leaves this device.
          </span>
          {avatar && (
            <button
              type="button"
              className="btn-text danger"
              onClick={() => {
                setAvatar(null);
                setSavedTick(false);
              }}
              disabled={busy}
            >
              Remove photo
            </button>
          )}
        </div>
      </div>

      <label className="field">
        <span>Display name</span>
        <input
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            setSavedTick(false);
          }}
          maxLength={MAX_PROFILE_NAME_CHARS}
          autoComplete="nickname"
          enterKeyHint="done"
          required
        />
        <span className="muted text-xs">{displayName.length}/{MAX_PROFILE_NAME_CHARS}</span>
      </label>

      <label className="field">
        <span>About</span>
        <textarea
          value={about}
          onChange={(e) => {
            setAbout(e.target.value.slice(0, MAX_ABOUT_CHARS));
            setSavedTick(false);
          }}
          maxLength={MAX_ABOUT_CHARS}
          rows={3}
          placeholder="Tell contacts a little about yourself"
          enterKeyHint="done"
        />
        <span className="muted text-xs">{about.length}/{MAX_ABOUT_CHARS}</span>
      </label>

      <p className="muted text-sm">
        Profile changes are end-to-end encrypted per contact and sent over the same ratchet
        sessions as messages — relays only ever carry ciphertext.
      </p>

      <div className="actions-row">
        {onClose && (
          <button
            className="btn secondary"
            type="button"
            onClick={() => {
              if (dirty) reset();
              onClose();
            }}
            disabled={busy}
          >
            {dirty ? "Discard" : "Done"}
          </button>
        )}
        <button className="btn" type="button" onClick={() => void save()} disabled={busy || !dirty || !nameOk}>
          {busy ? "Saving…" : dirty ? "Save profile" : savedTick ? "Saved ✓" : "Saved"}
        </button>
      </div>
    </div>
  );
}
